import * as React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { useField } from 'formik';
import { useState, useEffect } from 'react';
import AddressHistory, { createNewAddressHistory } from '../../../models/be/AddressHistory';
import { hasValue, single } from '../../../lib/utils';
import { Component } from '../../../models/be/db/FormSpecification';
import { getLocaleDateString } from '../../../core/internationalisation';
import EditButton from '../buttons/EditButton';
import DeleteButton from '../buttons/DeleteButton';
// eslint-disable-next-line import/no-cycle
import DataCapturePageSubForm from './DataCapturePageSubForm';
import './AddressHistoryEditor.less';
import {
	generateUpdatedAscendingAddressHistories,
	validateAddressHistoriesWithSelectedIndex,
	getAddressString,
	validateAddressHistoryEditor,
	getNoAddressContent
} from '../../../core/addressHistories';
import MessageBox from '../MessageBox';
import PrimaryButton from '../buttons/PrimaryButton';
import YesNoDialog from '../Dialogs/YesNoDialog';
import Workflow from '../../../models/be/Workflow';
import { getCountryName } from '../../../core/countryList';

const AddressHistoryEditor = (props: Props) => {
	const {
		hasCheckCountry,
		numberOfYearsToCover,
		countryRestriction,
		validationType,
		requiredCountry,
		visibleWorkflowFields
	} = props;

	const fieldName = single(props.fieldNames);
	const formikProps = { name: fieldName };
	const [field, meta, helpers] = useField(formikProps);
	const [showDialog, setShowDialog] = React.useState(false);
	const [errorMessage, setErrorMessage] = useState<string>(null);
	const [selectedIndex, setSelectedIndex] = useState<number>(null);
	const addressHistories = hasValue(field.value) ? (field.value as AddressHistory[]) : [];
	const [currentAddressHistory, setCurrentAddressHistory] = useState<AddressHistory>(null);
	const internationalCheckCountry =
		hasCheckCountry && visibleWorkflowFields
			? visibleWorkflowFields.icrmDetails?.criminalCheckCountry ?? visibleWorkflowFields.icrdDetails?.creditCheckCountry
			: null;

	const setNavigationButtonsDisabled = (value: boolean) => {
		props.setSubmitButtonsDisabled(value);
		props.setBackButtonsDisabled(value);
	};

	const [hasInitialized, setHasInitialized] = useState(false);

	const autoUpdateLatestAddressToToday = () => {
		if (addressHistories.length === 0 || hasInitialized) return;

		const today = getTodayDateString();
		const sortedHistories = [...addressHistories].sort(
			(a, b) => new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime()
		);
		const currentAddress = sortedHistories[0];

		let updatedHistories = addressHistories;
		if (currentAddress.toDate !== today) {
			updatedHistories = addressHistories.map((addr) => {
				if (addr === currentAddress) {
					return { ...addr, toDate: today };
				}
				return addr;
			});
			helpers.setValue(updatedHistories);
		}

		const descendingHistories = [...updatedHistories].sort(
			(a, b) => new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime()
		);
		const error = validateAddressHistoryEditor(
			descendingHistories,
			numberOfYearsToCover,
			countryRestriction,
			validationType,
			requiredCountry
		);
		setErrorMessage(error);
		if (hasValue(error)) setNavigationButtonsDisabled(true);

		setHasInitialized(true);
	};

	useEffect(() => {
		if (addressHistories.length > 0 && !hasInitialized) {
			autoUpdateLatestAddressToToday();
		}
	}, [addressHistories.length, hasInitialized]);

	const getTodayDateString = (): string => {
		const today = new Date();
		const year = today.getFullYear();
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const day = String(today.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	const getDateMinusOneDay = (dateString: string): string => {
		const date = new Date(`${dateString}T00:00:00`); // Ensure local timezone
		date.setDate(date.getDate() - 1);
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	useEffect(() => {
		if (addressHistories.length === 0 && currentAddressHistory === null && !ignoreAddressRange) {
			const newCurrentAddress = createNewAddressHistory();
			newCurrentAddress.toDate = getTodayDateString();
			setCurrentAddressHistory(newCurrentAddress);
			setNavigationButtonsDisabled(true);
		}
	}, [addressHistories.length]);

	const handleEditButtonClick = (index: number) => () => {
		setNavigationButtonsDisabled(true);
		setSelectedIndex(index);
		setCurrentAddressHistory(addressHistories[index]);
	};

	const handleDeleteButtonClick = (index: number) => async () => {
		setSelectedIndex(index);
		setShowDialog(true);
	};

	const handleYes = async () => {
		await deleteRecord(selectedIndex);
		setShowDialog(false);
	};

	const ignoreAddressRange = numberOfYearsToCover === 0;

	const deleteRecord = async (index: number) => {
		const updatedAddressHistories = addressHistories.filter((a, i) => i !== index);
		if (updatedAddressHistories?.length === 0) setNavigationButtonsDisabled(true);
		helpers.setValue(updatedAddressHistories);
		setSelectedIndex(null);
		const error = validateAddressHistoryEditor(
			updatedAddressHistories,
			numberOfYearsToCover,
			countryRestriction,
			validationType,
			requiredCountry
		);
		setErrorMessage(error);
		hasValue(error) ? setNavigationButtonsDisabled(true) : setNavigationButtonsDisabled(false);
	};

	const handleAddButtonClick = () => {
		setNavigationButtonsDisabled(true);
		const newAddressHistory = createNewAddressHistory();

		if (addressHistories.length > 0) {
			const sortedHistories = [...addressHistories].sort(
				(a, b) => new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime()
			);
			const earliestAddress = sortedHistories[0];
			newAddressHistory.toDate = getDateMinusOneDay(earliestAddress.fromDate);
		} else {
			newAddressHistory.toDate = getTodayDateString();
		}

		setCurrentAddressHistory(newAddressHistory);
	};

	const resetStatesOfClosingForm = () => {
		if (getAddressHistory?.length > 0) setNavigationButtonsDisabled(false);
		setSelectedIndex(null);
		setCurrentAddressHistory(null);
	};

	const handleSaveButtonClick = (values: { addressHistory: AddressHistory }) => {
		const addressToSave = { ...values.addressHistory };

		// Check if this is a new current address (toDate is today)
		const isNewCurrentAddress = addressToSave.toDate === getTodayDateString() && selectedIndex === null;

		if (selectedIndex === null && addressHistories.length > 0) {
			const sortedHistories = [...addressHistories].sort(
				(a, b) => new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime()
			);
			const earliestAddress = sortedHistories[0];
			addressToSave.toDate = getDateMinusOneDay(earliestAddress.fromDate);
		} else if (selectedIndex === null && addressHistories.length === 0) {
			addressToSave.toDate = getTodayDateString();
		}

		const updatedAddressHistories = generateUpdatedAscendingAddressHistories(
			addressHistories,
			addressToSave,
			selectedIndex,
			numberOfYearsToCover
		);

		const descendingOrderedHistories = updatedAddressHistories.sort(
			(a, b) => new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime()
		);

		// If adding a new current address, adjust previous addresses' toDate to avoid overlap
		if (isNewCurrentAddress && descendingOrderedHistories.length > 1) {
			const newCurrentAddress = descendingOrderedHistories[0];
			const previousAddress = descendingOrderedHistories[1];

			// Set the previous address's toDate to one day before the new current address's fromDate
			if (newCurrentAddress.fromDate) {
				previousAddress.toDate = getDateMinusOneDay(newCurrentAddress.fromDate);
			}
		}

		// Only set the most recent address to today's date if it's a new address being added
		// Don't override user's manual date changes when editing existing addresses
		if (selectedIndex === null && descendingOrderedHistories.length > 0) {
			descendingOrderedHistories[0].toDate = getTodayDateString();
		}

		helpers.setValue(descendingOrderedHistories);
		resetStatesOfClosingForm();
		const error = validateAddressHistoryEditor(
			descendingOrderedHistories,
			numberOfYearsToCover,
			countryRestriction,
			validationType,
			requiredCountry
		);
		setErrorMessage(error);
		hasValue(error) ? setNavigationButtonsDisabled(true) : setNavigationButtonsDisabled(false);
	};

	const checkCountry = getCountryName(internationalCheckCountry);
	const getAddressHistory = !ignoreAddressRange
		? addressHistories
		: addressHistories
				.sort((a, b) => (a.toDate > b.toDate ? 1 : -1))
				.reverse()
				.slice(0, 1);

	const warningMsg = `At least one address from ${checkCountry} is required.`;

	const hasCountryAddressRequirement = (): boolean => {
		if (!hasCheckCountry || !checkCountry) return true;
		const hasRequiredCountry = addressHistories.some((address) => {
			return address.country === internationalCheckCountry || address.country === checkCountry;
		});
		return hasRequiredCountry;
	};

	const hasAddressFromCheckCountry = (): boolean => {
		if (!internationalCheckCountry || addressHistories.length === 0) return false;
		return addressHistories.some((address) => address.country === internationalCheckCountry);
	};

	const shouldShowCountryWarning = hasCheckCountry && checkCountry && !hasAddressFromCheckCountry();

	const isAddingNewAddress = selectedIndex === null && currentAddressHistory !== null;

	const AddNewAddress = (
		<div className={!ignoreAddressRange && !props.showTwoColumnFields ? 'arrayItem' : null}>
			<div
				className={classNames(
					!ignoreAddressRange && !props.showTwoColumnFields ? 'sectionContainer addAddressHistory expanded' : null
				)}
			>
				{isAddingNewAddress ? (
					<DataCapturePageSubForm
						values={{ addressHistory: currentAddressHistory }}
						components={props.components}
						validate={validateAddressHistoriesWithSelectedIndex(addressHistories, selectedIndex, ignoreAddressRange)}
						onSaveButtonClick={handleSaveButtonClick}
						onCancelButtonClick={resetStatesOfClosingForm}
						saveButtonText={addressHistories.length === 0 ? 'Add Current Address' : 'Add'}
						isToDateReadOnly
					/>
				) : (
					<>
						<PrimaryButton
							text={addressHistories.length === 0 ? 'Add Current Address' : 'Add Address'}
							onClick={handleAddButtonClick}
						/>
						{!ignoreAddressRange && addressHistories.length === 0 && (
							<Message
								dangerouslySetInnerHTML={{
									__html: getNoAddressContent(numberOfYearsToCover).replace(/\n/g, '<br/>')
								}}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);

	return (
		<>
			{showDialog && (
				<YesNoDialog
					heading="Delete address history"
					message="Are you sure you want to delete this record?"
					onYes={handleYes}
					onNo={() => setShowDialog(false)}
					yesButtonText="Delete"
					noButtonText="Cancel"
					yesButtonIsDelete
				/>
			)}
			{meta.error && !errorMessage && <MessageBox error={meta.touched ? meta.error : undefined} />}
			{errorMessage && <MessageBox error={errorMessage} />}
			{hasCheckCountry && checkCountry && (!hasCountryAddressRequirement() || shouldShowCountryWarning) && (
				<MessageBox information={warningMsg} />
			)}
			<Header>Address History</Header>
			{!ignoreAddressRange && (
				<LegendContainer>
					<Legend>
						Please enter {numberOfYearsToCover} years of address history, starting with your current address and then
						the next oldest. Failure to disclose any address may result in your application being withdrawn so please
						ensure the information you provide is accurate and complete before submission.
					</Legend>
				</LegendContainer>
			)}
			{getAddressHistory?.map((x, i) => (
				<div className={ignoreAddressRange || props.showTwoColumnFields ? '' : 'arrayItem'} key={x.fromDate}>
					{selectedIndex === i ? (
						<div className={ignoreAddressRange || props.showTwoColumnFields ? '' : 'sectionContainer expanded'}>
							<DataCapturePageSubForm
								values={{ addressHistory: x }}
								components={props.components}
								onSaveButtonClick={handleSaveButtonClick}
								onCancelButtonClick={resetStatesOfClosingForm}
								saveButtonText="Update"
								validate={validateAddressHistoriesWithSelectedIndex(
									addressHistories,
									selectedIndex,
									ignoreAddressRange
								)}
								isToDateReadOnly={false}
							/>
						</div>
					) : (
						<>
							<legend>
								{x.fromDate && (
									<>
										from <span className="bold">{getLocaleDateString(x.fromDate)} </span>
									</>
								)}
								{x.toDate && (
									<>
										to <span className="bold">{getLocaleDateString(x.toDate)}</span>
									</>
								)}
							</legend>
							<div className="sectionContainer">
								{currentAddressHistory === null && (
									<div className={ignoreAddressRange || props.showTwoColumnFields ? 'alignRight' : ''}>
										{!ignoreAddressRange ? <DeleteButton onClick={handleDeleteButtonClick(i)} /> : null}
										<EditButton onClick={handleEditButtonClick(i)} />
									</div>
								)}
								<p className="address">{getAddressString(x)}</p>
							</div>
						</>
					)}
				</div>
			))}
			{ignoreAddressRange && getAddressHistory.length === 0 && selectedIndex === null && AddNewAddress}
			{!ignoreAddressRange && selectedIndex === null && AddNewAddress}
		</>
	);
};

interface Props {
	fieldNames: string[];
	components: Component[];
	numberOfYearsToCover: number;
	setSubmitButtonsDisabled?: (disabled: boolean) => void;
	setBackButtonsDisabled?: (disabled: boolean) => void;
	hasCheckCountry?: boolean;
	countryRestriction?: string;
	validationType?: string;
	requiredCountry?: string;
	showTwoColumnFields?: boolean;
	visibleWorkflowFields?: Workflow;
}

const Message = styled.p`
	margin-left: 5px;
	margin-top: 15px !important;
	display: block;
`;

const LegendContainer = styled.div`
	margin-top: 10px !important;
	margin-bottom: 10px !important;
	display: block;
	legend {
		color: #262626;
		opacity: 0.75;
		font-size: 14px;
	}
`;

const Legend = styled.legend`
	text-transform: none !important;
`;

const Header = styled.legend`
	font-size: 13px;
	line-height: 16px;
	text-transform: uppercase;
	opacity: 0.65;
	color: #262626;
	margin: 0 0 9px 0;
	text-align: left;
	padding: 12px 16px 0 0px;
	background-color: #f8f7f3;
	width: 100%;
	font-weight: 400;
`;

export default AddressHistoryEditor;

AddressHistoryEditor.defaultProps = {
	hasCheckCountry: false,
	countryRestriction: null,
	validationType: null,
	requiredCountry: null,
	showTwoColumnFields: false,
	setSubmitButtonsDisabled: null,
	setBackButtonsDisabled: null,
	visibleWorkflowFields: null
};
